import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BatteryCharging,
  Cpu,
  Layers,
  Plus,
  Circle,
  Trash2,
  Loader2,
  X,
  FolderPlus,
  Folder,
  Pencil,
  Check,
  GripVertical,
  Share2,
  Mail,
} from "lucide-react";
import { toast } from "sonner";
import TopNav from "@/components/TopNav";
import { useAuth } from "@/context/AuthContext";
import {
  getDevices,
  createDevice,
  deleteDevice,
  getGroups,
  createGroup,
  deleteGroup,
  updateGroup,
  reorderGroups,
  updateDeviceGroup,
  updateDevice,
  getDeviceShares,
  shareDevice,
  unshareDevice,
  formatApiError,
} from "@/lib/api";

const relTime = (iso) => {
  if (!iso) return "немає даних";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.round(s)} с тому`;
  if (s < 3600) return `${Math.round(s / 60)} хв тому`;
  return `${Math.round(s / 3600)} год тому`;
};

const isOnline = (iso) => iso && Date.now() - new Date(iso).getTime() < 60000;

function AddDeviceModal({ onClose, onCreated, groups }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("battery");
  const [topic, setTopic] = useState("");
  const [groupId, setGroupId] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const d = await createDevice({ name, type, mqtt_topic: topic, group_id: groupId || null });
      toast.success(`Прилад "${d.name}" додано`);
      onCreated();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className="relative z-10 w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-lg"
        data-testid="add-device-modal"
      >
        <div className="mb-5 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-slate-900">
            Новий прилад
          </h2>
          <button
            data-testid="add-device-close"
            onClick={onClose}
            className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100"
          >
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Назва
            </label>
            <input
              data-testid="add-device-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              placeholder="Напр. Міська Рада"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Тип
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "battery", l: "АКБ", icon: BatteryCharging },
                { v: "inverter", l: "Інвертор", icon: Cpu },
                { v: "viwat_hv", l: "Viwat HV", icon: Layers },
              ].map(({ v, l, icon: Icon }) => (
                <button
                  key={v}
                  type="button"
                  data-testid={`add-device-type-${v}`}
                  onClick={() => setType(v)}
                  className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${
                    type === v
                      ? "border-blue-500 bg-blue-50 text-blue-700"
                      : "border-slate-300 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <Icon size={16} />
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              MQTT топік
            </label>
            <input
              data-testid="add-device-topic"
              required
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 font-mono text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              placeholder="hosts/SN112233445566"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Група
            </label>
            <select
              data-testid="add-device-group"
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="">Без групи</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </div>
          <button
            data-testid="add-device-submit"
            type="submit"
            disabled={saving}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
          >
            {saving && <Loader2 size={16} className="animate-spin" />}
            Додати прилад
          </button>
        </form>
      </div>
    </div>
  );
}

function CreateGroupModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const g = await createGroup(name);
      toast.success(`Групу "${g.name}" створено`);
      onCreated();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-lg" data-testid="add-group-modal">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-slate-900">Нова група</h2>
          <button onClick={onClose} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <input
            data-testid="add-group-name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            placeholder="Напр. Давидівська школа"
          />
          <button data-testid="add-group-submit" type="submit" disabled={saving}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
            {saving && <Loader2 size={16} className="animate-spin" />}
            Створити групу
          </button>
        </form>
      </div>
    </div>
  );
}

function EditDeviceModal({ device, onClose, onUpdated, groups }) {
  const [name, setName] = useState(device.name);
  const [type, setType] = useState(device.type);
  const [topic, setTopic] = useState(device.mqtt_topic);
  const [groupId, setGroupId] = useState(device.group_id || "");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateDevice(device.id, { name, type, mqtt_topic: topic, group_id: groupId || null });
      toast.success("Прилад оновлено");
      onUpdated();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-lg" data-testid="edit-device-modal">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-slate-900">Редагувати прилад</h2>
          <button data-testid="edit-device-close" onClick={onClose} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">Назва</label>
            <input
              data-testid="edit-device-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">Тип</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "battery", l: "АКБ", icon: BatteryCharging },
                { v: "inverter", l: "Інвертор", icon: Cpu },
                { v: "viwat_hv", l: "Viwat HV", icon: Layers },
              ].map(({ v, l, icon: Icon }) => (
                <button
                  key={v}
                  type="button"
                  data-testid={`edit-device-type-${v}`}
                  onClick={() => setType(v)}
                  className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${
                    type === v ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-300 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <Icon size={16} />
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">MQTT топік</label>
            <input
              data-testid="edit-device-topic"
              required
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 font-mono text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">Група</label>
            <select
              data-testid="edit-device-group"
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="">Без групи</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </div>
          <button
            data-testid="edit-device-submit"
            type="submit"
            disabled={saving}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
          >
            {saving && <Loader2 size={16} className="animate-spin" />}
            Зберегти зміни
          </button>
        </form>
      </div>
    </div>
  );
}

function ShareDeviceModal({ device, onClose }) {
  const [shares, setShares] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () =>
    getDeviceShares(device.id)
      .then(setShares)
      .catch(() => {})
      .finally(() => setLoading(false));
  useEffect(() => {
    load();
  }, []);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await shareDevice(device.id, email.trim());
      toast.success(`Доступ надано ${email.trim()}`);
      setEmail("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    try {
      await unshareDevice(device.id, u.id);
      toast.success(`Доступ для ${u.email} прибрано`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative z-10 w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-lg"
        data-testid="share-device-modal"
      >
        <div className="mb-5 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-slate-900">
            Поділитися · {device.name}
          </h2>
          <button
            data-testid="share-device-close"
            onClick={onClose}
            className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={add} className="mb-5 flex gap-2">
          <div className="relative flex-1">
            <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="share-device-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              placeholder="email користувача"
            />
          </div>
          <button
            data-testid="share-device-submit"
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
            Додати
          </button>
        </form>

        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Має доступ
        </div>
        <div className="mt-2 space-y-2">
          {loading ? (
            <div className="flex justify-center py-4 text-slate-400">
              <Loader2 size={18} className="animate-spin" />
            </div>
          ) : shares.length ? (
            shares.map((u) => (
              <div
                key={u.id}
                data-testid={`share-row-${u.id}`}
                className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
              >
                <div>
                  <div className="text-sm font-medium text-slate-800">{u.name || u.email}</div>
                  <div className="text-xs text-slate-500">{u.email}</div>
                </div>
                <button
                  data-testid={`share-remove-${u.id}`}
                  onClick={() => remove(u)}
                  className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-500"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))
          ) : (
            <div className="rounded-lg border border-dashed border-slate-300 py-4 text-center text-sm text-slate-400">
              Ще нікому не надано доступ
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DeviceCard({ device, onOpen, onDelete, onEdit, onShare, canManage }) {
  const isViwat = device.type === "viwat_hv";
  const battery = device.type === "battery" || isViwat;
  const Icon = isViwat ? Layers : battery ? BatteryCharging : Cpu;
  const typeLabel = isViwat ? "Viwat HV" : battery ? "АКБ" : "Інвертор";
  const online = isOnline(device.last_seen);
  const v = device.last_values || {};
  const stats = battery
    ? [
        { l: "SOC", val: v.soc != null ? `${v.soc}%` : "—" },
        { l: "Напруга", val: v.total_voltage != null ? `${v.total_voltage} В` : "—" },
        { l: "Струм", val: v.current != null ? `${v.current} А` : "—" },
      ]
    : [
        { l: "Потужність", val: v.power != null ? `${v.power} Вт` : "—" },
        { l: "PV", val: v.pv_power != null ? `${v.pv_power} Вт` : "—" },
        { l: "Батарея", val: v.battery != null ? `${v.battery}%` : "—" },
      ];

  return (
    <div
      data-testid={`device-card-${device.id}`}
      onClick={() => onOpen(device)}
      className="group cursor-pointer rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-colors hover:bg-slate-50"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <span
            className={`flex h-11 w-11 items-center justify-center rounded-lg ${
              battery ? "bg-emerald-50 text-emerald-600" : "bg-blue-50 text-blue-600"
            }`}
          >
            <Icon size={22} />
          </span>
          <div>
            <div className="font-display text-base font-bold text-slate-900">
              {device.name}
            </div>
            <div className="text-xs uppercase tracking-wide text-slate-400">
              {typeLabel}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 opacity-0 transition-all group-hover:opacity-100">
          {canManage && (
            <>
              <button
                data-testid={`device-share-${device.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onShare(device);
                }}
                title="Поділитися"
                className="rounded-md p-1.5 text-slate-300 transition-colors hover:bg-emerald-50 hover:text-emerald-600"
              >
                <Share2 size={16} />
              </button>
              <button
                data-testid={`device-edit-${device.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(device);
                }}
                className="rounded-md p-1.5 text-slate-300 transition-colors hover:bg-blue-50 hover:text-blue-600"
              >
                <Pencil size={16} />
              </button>
              <button
                data-testid={`device-delete-${device.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(device);
                }}
                className="rounded-md p-1.5 text-slate-300 transition-colors hover:bg-red-50 hover:text-red-500"
              >
                <Trash2 size={16} />
              </button>
            </>
          )}
          {!canManage && (
            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-600">
              Спільний
            </span>
          )}
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 text-xs">
        <Circle
          size={8}
          className={online ? "fill-emerald-500 text-emerald-500" : "fill-slate-300 text-slate-300"}
        />
        <span className={online ? "font-medium text-emerald-600" : "text-slate-400"}>
          {online ? "В мережі" : "Немає звʼязку"}
        </span>
        <span className="text-slate-400">· {relTime(device.last_seen)}</span>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 border-t border-slate-100 pt-4">
        {stats.map((s) => (
          <div key={s.l}>
            <div className="text-[10px] uppercase tracking-wide text-slate-400">
              {s.l}
            </div>
            <div className="font-mono text-sm font-semibold text-slate-900">
              {s.val}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 truncate font-mono text-[11px] text-slate-400">
        {device.mqtt_topic}
      </div>
    </div>
  );
}

export default function Devices() {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [devices, setDevices] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sharing, setSharing] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const load = () =>
    Promise.all([getDevices(), getGroups()])
      .then(([d, g]) => {
        setDevices(d);
        setGroups(g);
      })
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, []);

  const open = (d) =>
    navigate(
      d.type === "inverter"
        ? `/devices/${d.id}/inverter`
        : `/devices/${d.id}/battery`
    );

  const remove = async (d) => {
    if (!window.confirm(`Видалити прилад "${d.name}"?`)) return;
    try {
      await deleteDevice(d.id);
      toast.success("Прилад видалено");
      load();
    } catch {
      toast.error("Не вдалося видалити");
    }
  };

  const move = async (d, groupId) => {
    try {
      await updateDeviceGroup(d.id, groupId);
      load();
    } catch {
      toast.error("Не вдалося перемістити");
    }
  };

  const removeGroup = async (g) => {
    if (!window.confirm(`Видалити групу "${g.name}"? Прилади залишаться без групи.`)) return;
    try {
      await deleteGroup(g.id);
      toast.success("Групу видалено");
      load();
    } catch {
      toast.error("Не вдалося видалити групу");
    }
  };

  const startRename = (g) => {
    setRenamingId(g.id);
    setRenameVal(g.name);
  };
  const cancelRename = () => {
    setRenamingId(null);
    setRenameVal("");
  };
  const saveRename = async (g) => {
    const name = renameVal.trim();
    if (!name || name === g.name) {
      cancelRename();
      return;
    }
    try {
      await updateGroup(g.id, name);
      toast.success("Назву групи оновлено");
      cancelRename();
      load();
    } catch {
      toast.error("Не вдалося оновити назву");
    }
  };

  const onGroupDrop = async (targetId) => {
    const from = dragId;
    setDragId(null);
    setDragOverId(null);
    if (!from || from === targetId) return;
    const ordered = [...groups];
    const fromIdx = ordered.findIndex((g) => g.id === from);
    const toIdx = ordered.findIndex((g) => g.id === targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    const [moved] = ordered.splice(fromIdx, 1);
    ordered.splice(toIdx, 0, moved);
    setGroups(ordered); // optimistic
    try {
      await reorderGroups(ordered.map((g) => g.id));
    } catch {
      toast.error("Не вдалося змінити порядок груп");
      load();
    }
  };

  const cardsGrid = (items) => (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((d) => (
        <DeviceCard
          key={d.id}
          device={d}
          onOpen={open}
          onDelete={remove}
          onEdit={setEditing}
          onShare={setSharing}
          canManage={me?.role === "admin" || d.owner_id === me?.id}
        />
      ))}
    </div>
  );

  const ungrouped = devices.filter(
    (d) => !d.group_id || !groups.some((g) => g.id === d.group_id)
  );

  return (
    <div className="min-h-screen bg-slate-100 pt-16">
      <TopNav />
      <div className="mx-auto max-w-[1500px] px-6 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">
              Прилади
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {groups.length} груп · {devices.length}{" "}
              {devices.length === 1 ? "прилад" : "приладів"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="add-group-btn"
              onClick={() => setShowAddGroup(true)}
              className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
            >
              <FolderPlus size={18} />
              Створити групу
            </button>
            <button
              data-testid="add-device-btn"
              onClick={() => setShowAdd(true)}
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
            >
              <Plus size={18} />
              Додати прилад
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-400">
            <Loader2 size={24} className="animate-spin" />
          </div>
        ) : (
          <div data-testid="devices-grid" className="space-y-8">
            {groups.map((g) => {
              const items = devices.filter((d) => d.group_id === g.id);
              return (
                <section
                  key={g.id}
                  data-testid={`group-${g.id}`}
                  onDragOver={(e) => {
                    if (dragId) {
                      e.preventDefault();
                      if (dragOverId !== g.id) setDragOverId(g.id);
                    }
                  }}
                  onDragLeave={() => {
                    if (dragOverId === g.id) setDragOverId(null);
                  }}
                  onDrop={() => onGroupDrop(g.id)}
                  className={`rounded-xl transition-all ${
                    dragOverId === g.id && dragId !== g.id
                      ? "ring-2 ring-blue-400 ring-offset-2"
                      : ""
                  } ${dragId === g.id ? "opacity-40" : ""}`}
                >
                  <div className="mb-3 flex items-center gap-2">
                    <span
                      draggable
                      onDragStart={() => setDragId(g.id)}
                      onDragEnd={() => {
                        setDragId(null);
                        setDragOverId(null);
                      }}
                      data-testid={`group-drag-${g.id}`}
                      title="Перетягніть, щоб змінити порядок"
                      className="cursor-grab text-slate-300 transition-colors hover:text-slate-500 active:cursor-grabbing"
                    >
                      <GripVertical size={18} />
                    </span>
                    <Folder size={18} className="text-blue-600" />
                    {renamingId === g.id ? (
                      <div className="flex items-center gap-1">
                        <input
                          data-testid={`group-rename-input-${g.id}`}
                          autoFocus
                          value={renameVal}
                          onChange={(e) => setRenameVal(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveRename(g);
                            if (e.key === "Escape") cancelRename();
                          }}
                          className="rounded-md border border-blue-300 px-2 py-1 text-lg font-bold text-slate-900 outline-none focus:ring-2 focus:ring-blue-200"
                        />
                        <button
                          data-testid={`group-rename-save-${g.id}`}
                          onClick={() => saveRename(g)}
                          className="rounded-md p-1 text-emerald-600 transition-colors hover:bg-emerald-50"
                        >
                          <Check size={17} />
                        </button>
                        <button
                          data-testid={`group-rename-cancel-${g.id}`}
                          onClick={cancelRename}
                          className="rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100"
                        >
                          <X size={17} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <h2 className="font-display text-lg font-bold text-slate-900">{g.name}</h2>
                        <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-600">
                          {items.length}
                        </span>
                        <button
                          data-testid={`group-rename-${g.id}`}
                          onClick={() => startRename(g)}
                          className="ml-1 rounded-md p-1 text-slate-300 transition-colors hover:bg-blue-50 hover:text-blue-500"
                          title="Перейменувати групу"
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          data-testid={`group-delete-${g.id}`}
                          onClick={() => removeGroup(g)}
                          className="rounded-md p-1 text-slate-300 transition-colors hover:bg-red-50 hover:text-red-500"
                          title="Видалити групу"
                        >
                          <Trash2 size={15} />
                        </button>
                      </>
                    )}
                  </div>
                  {items.length ? (
                    cardsGrid(items)
                  ) : (
                    <div className="rounded-xl border border-dashed border-slate-300 py-8 text-center text-sm text-slate-400">
                      Немає приладів у цій групі
                    </div>
                  )}
                </section>
              );
            })}

            {ungrouped.length > 0 && (
              <section data-testid="group-none">
                <div className="mb-3 flex items-center gap-2">
                  <Folder size={18} className="text-slate-400" />
                  <h2 className="font-display text-lg font-bold text-slate-700">Без групи</h2>
                  <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-600">
                    {ungrouped.length}
                  </span>
                </div>
                {cardsGrid(ungrouped)}
              </section>
            )}
          </div>
        )}
      </div>

      {showAdd && (
        <AddDeviceModal groups={groups} onClose={() => setShowAdd(false)} onCreated={load} />
      )}
      {showAddGroup && (
        <CreateGroupModal onClose={() => setShowAddGroup(false)} onCreated={load} />
      )}
      {editing && (
        <EditDeviceModal
          device={editing}
          groups={groups}
          onClose={() => setEditing(null)}
          onUpdated={load}
        />
      )}
      {sharing && (
        <ShareDeviceModal device={sharing} onClose={() => setSharing(null)} />
      )}
    </div>
  );
}
